<template>
  <div class="admin-layout">
    <aside class="sidebar" :class="{ collapsed: uiStore.sidebarCollapsed }">
      <div class="sidebar-brand" aria-label="AskSpinner CMS">
        <span v-if="uiStore.sidebarCollapsed" class="sidebar-monogram">AS</span>
        <template v-else>
          <img src="@/assets/askSpinnerLogo.png" alt="AskSpinner" />
          <span class="sidebar-brand-badge">CMS</span>
        </template>
      </div>

      <p v-if="!uiStore.sidebarCollapsed" class="sidebar-section-label">管理工作台</p>
      <el-menu :default-active="activeMenuPath" router :collapse="uiStore.sidebarCollapsed" class="admin-menu">
        <AdminSidebarMenu v-for="menu in currentSystemMenus" :key="menu.key" :menu="menu" />
      </el-menu>

      <div class="sidebar-profile" aria-label="当前登录用户">
        <span class="sidebar-profile-avatar">{{ userInitials }}</span>
        <div v-if="!uiStore.sidebarCollapsed" class="sidebar-profile-copy">
          <strong>{{ authStore.displayName || '管理员' }}</strong>
          <span>{{ currentRoleLabel }}</span>
        </div>
        <el-dropdown trigger="click" placement="top-end" popper-class="sidebar-user-dropdown">
          <button class="sidebar-profile-menu" type="button" aria-label="打开用户操作菜单">
            <el-icon><MoreFilled /></el-icon>
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="handleLogout">
                <el-icon><SwitchButton /></el-icon>
                <span>退出登录</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </aside>

    <section class="layout-main">
      <header class="topbar">
        <div class="topbar-context">
          <button class="collapse-button" type="button" aria-label="切换侧栏" @click="uiStore.toggleSidebar">
            <el-icon><Fold v-if="!uiStore.sidebarCollapsed" /><Expand v-else /></el-icon>
          </button>

          <div class="topbar-brand" aria-label="AskSpinner 统一管理平台">
            <strong class="topbar-title">AskSpinner 统一管理平台</strong>
            <span class="topbar-scope-badge">{{ currentSystemBadgeLabel }}</span>
          </div>

          <span class="topbar-divider" aria-hidden="true"></span>

          <el-dropdown
            trigger="click"
            popper-class="timezone-dropdown-popper"
            :disabled="timezoneStore.loading || !timezoneStore.options.length"
            @command="changeTimezone"
          >
            <button class="system-switcher timezone-switcher" type="button" aria-label="切换中台展示时区">
              <el-icon class="switcher-leading-icon"><Clock /></el-icon>
              <span class="switcher-label">时区：</span>
              <strong>{{ timezoneStore.currentDisplayName }}</strong>
              <el-icon class="switcher-caret"><CaretBottom /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu class="timezone-dropdown-menu">
                <el-dropdown-item
                  v-for="item in timezoneStore.options"
                  :key="item.timezoneId"
                  :command="item.timezoneId"
                  :class="{ 'is-current-timezone': item.timezoneId === timezoneStore.currentTimezone }"
                >
                  <el-icon v-if="item.timezoneId === timezoneStore.currentTimezone"><Check /></el-icon>
                  <span :class="{ 'system-option-indent': item.timezoneId !== timezoneStore.currentTimezone }">
                    {{ item.displayName }}
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-dropdown
            trigger="click"
            :disabled="siteContextStore.loading || !siteContextStore.options.length"
            @command="changeSiteContext"
          >
            <button class="system-switcher site-context-switcher" type="button" aria-label="切换当前国家站">
              <span class="switcher-label">当前国家站：</span>
              <strong>{{ currentSiteHeaderLabel }}</strong>
              <el-icon class="switcher-caret"><CaretBottom /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu class="site-context-dropdown-menu">
                <template v-for="country in siteContextStore.countries" :key="country.countryId">
                  <li class="site-context-country" role="presentation">{{ country.countryName }}</li>
                  <el-dropdown-item
                    v-for="item in country.sites"
                    :key="item.siteId"
                    :command="item.siteId"
                    :class="{ 'is-current-site': item.siteId === siteContextStore.currentSiteId }"
                  >
                    <el-icon v-if="item.siteId === siteContextStore.currentSiteId"><Check /></el-icon>
                    <span :class="{ 'system-option-indent': item.siteId !== siteContextStore.currentSiteId }">
                      {{ item.siteName }} · {{ item.languageName }}
                    </span>
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <section class="workspace">
        <nav v-if="openMenuTabs.length > 0" class="page-tabs" aria-label="已打开菜单">
          <div ref="tabsListRef" class="page-tabs-list" @wheel="scrollTabsHorizontally">
            <button
              v-for="tab in openMenuTabs"
              :key="tab.path"
              class="page-tab"
              :class="{ active: tab.path === route.path }"
              type="button"
              @click="router.push(tab.fullPath)"
            >
              <span class="page-tab-dot"></span>
              <span>{{ tab.title }}</span>
              <el-icon class="page-tab-close" aria-label="关闭标签" @click.stop="closeTab(tab.path)">
                <Close />
              </el-icon>
            </button>
          </div>
          <button
            class="close-all-tabs"
            type="button"
            :disabled="openMenuTabs.length === 0"
            aria-label="关闭全部菜单标签"
            @click="closeAllTabs"
          >
            <el-icon><CircleClose /></el-icon>
            <span>关闭全部</span>
          </button>
        </nav>

        <div v-if="!isWelcomeRoute" class="content-toolbar">
          <span class="breadcrumb-label">当前位置：</span>
          <el-breadcrumb separator="/">
            <el-breadcrumb-item>{{ currentSystemLabel }}</el-breadcrumb-item>
            <el-breadcrumb-item v-if="currentParentTitle">{{ currentParentTitle }}</el-breadcrumb-item>
            <el-breadcrumb-item>{{ currentPageTitle }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <main class="main-content" :class="{ 'main-content-welcome': isWelcomeRoute }">
          <router-view v-slot="{ Component, route: viewRoute }">
            <component :is="Component" v-if="viewRoute.path === ADMIN_WELCOME_PATH" />
            <KeepAlive key="admin-menu-page-cache" :include="cachedMenuTabNames" :max="MAX_OPEN_MENU_TABS">
              <component
                :is="resolveCachedRouteComponent(Component, viewRoute.path)"
                v-if="viewRoute.path !== ADMIN_WELCOME_PATH"
                :key="menuTabCacheKey(viewRoute.path)"
                :route-component="Component"
              />
            </KeepAlive>
          </router-view>
        </main>
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  cloneVNode,
  computed,
  defineComponent,
  markRaw,
  nextTick,
  provide,
  ref,
  shallowReactive,
  shallowRef,
  watch,
  type Component as VueComponent,
  type PropType,
  type VNode
} from 'vue'
import { routeLocationKey, useRoute, useRouter, type RouteLocationNormalizedLoaded } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ADMIN_WELCOME_PATH } from '@/config/adminNavigation'
import AdminSidebarMenu from '@/layouts/components/AdminSidebarMenu.vue'
import { getErrorMessage } from '@/services/http'
import { useAuthStore } from '@/stores/auth'
import { useSiteContextStore } from '@/stores/siteContext'
import { useTimezoneStore } from '@/stores/timezone'
import {
  adminMenuCatalog,
  getAdminMenuRouteItem,
  getFrontendOnlyMenuRouteItems,
  getMenuRouteItems,
  hasRegisteredMenuRoute,
  isSameAdminMenuCode,
  type AdminMenuItem
} from '@/config/adminMenus'
import { normalizeAuthorizedMenuHierarchy } from '@/config/adminMenuHierarchy'
import { MAX_OPEN_MENU_TABS, useUiStore } from '@/stores/ui'
import type { AdminId, AdminMenu } from '@/types/auth'
import type { CenterId } from '@/types/businessCenter'

interface RuntimeMenuItem extends AdminMenuItem {
  id: AdminId
  parentId: AdminId
  type: AdminMenu['type']
  children?: RuntimeMenuItem[]
}

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const siteContextStore = useSiteContextStore()
const timezoneStore = useTimezoneStore()
const uiStore = useUiStore()
const tabsListRef = ref<HTMLElement>()
const menuPageCacheScope = ref(0)
const cachedRouteComponents = new Map<string, { source: VNode['type']; component: VueComponent }>()
const authorizedMenuRoots = computed(() =>
  buildAuthorizedMenuTree(authStore.currentUser?.menus || [], authStore.currentUser?.permissions || [])
)
const currentSystemRoot = computed(() => {
  const menuCode = typeof route.meta.menuCode === 'string' ? route.meta.menuCode : undefined
  const activePath = String(route.meta.activeMenu || route.path)
  return authorizedMenuRoots.value.find(root => containsActiveMenu(root, menuCode, activePath)) || authorizedMenuRoots.value[0]
})
const currentSystemLabel = computed(() => currentSystemRoot.value?.title || '中台业务管理')
const currentSystemMenus = computed(() => authorizedMenuRoots.value)
const activeMenuTrail = computed(() => resolveMenuTrail(route))
const currentPageTitle = computed(
  () => activeMenuTrail.value.at(-1)?.title || String(route.meta.title || currentSystemLabel.value)
)
const currentParentTitle = computed(() => {
  const trail = activeMenuTrail.value
  const parent = trail.at(-2)
  return parent && parent !== trail[0] ? parent.title : route.meta.parentTitle ? String(route.meta.parentTitle) : undefined
})
const openMenuTabs = computed(() => uiStore.openMenuTabs)
const cachedMenuTabNames = computed(() => openMenuTabs.value.map(tab => getMenuTabCacheName(tab.path)))
const isWelcomeRoute = computed(() => route.path === ADMIN_WELCOME_PATH)
const activeMenuPath = computed(() => (isWelcomeRoute.value ? '' : String(route.meta.activeMenu || route.path)))
const isCountrySiteRoute = computed(() => route.path === '/country-site' || route.path.startsWith('/country-site/'))
const currentSystemBadgeLabel = computed(() => (isCountrySiteRoute.value ? '国家站管理' : '中台管理'))
const currentSiteHeaderLabel = computed(() => {
  for (const country of siteContextStore.countries) {
    const site = country.sites.find(item => item.siteId === siteContextStore.currentSiteId)
    if (site) return `${country.countryCode.toUpperCase()} ${site.siteName} · ${site.languageName}`
  }
  return siteContextStore.loading ? '加载中' : '暂无可用站点'
})
const currentRoleLabel = computed(() => {
  const role = authStore.currentUser?.roles[0]
  if (role === 'SUPER_ADMIN') return '超级管理员'
  if (role === 'ADMIN') return '管理员'
  return role || '管理员'
})
const userInitials = computed(() => {
  const name = (authStore.displayName || authStore.currentUser?.username || 'AS').trim()
  const words = name.split(/\s+/).filter(Boolean)
  if (words.length > 1)
    return words
      .slice(0, 2)
      .map(word => word[0]?.toUpperCase())
      .join('')
  return name.slice(0, 2).toUpperCase()
})

function containsActiveMenu(item: RuntimeMenuItem, menuCode: string | undefined, activePath: string): boolean {
  if (item.type === 'MENU' && ((menuCode && isSameAdminMenuCode(item.menuCode, menuCode)) || item.path === activePath)) {
    return true
  }
  return (item.children || []).some(child => containsActiveMenu(child, menuCode, activePath))
}

function findActiveMenuTrail(
  items: RuntimeMenuItem[],
  menuCode: string | undefined,
  activePath: string,
  ancestors: RuntimeMenuItem[] = []
): RuntimeMenuItem[] | undefined {
  for (const item of items) {
    const trail = [...ancestors, item]
    if (item.type === 'MENU' && ((menuCode && isSameAdminMenuCode(item.menuCode, menuCode)) || item.path === activePath)) {
      return trail
    }
    const childTrail = findActiveMenuTrail(item.children || [], menuCode, activePath, trail)
    if (childTrail) return childTrail
  }
}

function resolveMenuTrail(location: { path: string; meta: Record<string | number | symbol, unknown> }) {
  const menuCode = typeof location.meta.menuCode === 'string' ? location.meta.menuCode : undefined
  const activePath = String(location.meta.activeMenu || location.path)
  return findActiveMenuTrail(authorizedMenuRoots.value, menuCode, activePath) || []
}

function syncOpenMenuTabNames() {
  openMenuTabs.value.forEach(tab => {
    const resolved = router.resolve(tab.fullPath)
    const trail = resolveMenuTrail(resolved)
    const menu = trail.at(-1)
    if (!menu) return
    const parent = trail.at(-2)
    uiStore.openMenuTab({
      ...tab,
      title: menu.title,
      parentTitle: parent && parent !== trail[0] ? parent.title : undefined
    })
  })
}

function buildAuthorizedMenuTree(menus: AdminMenu[], permissions: string[]) {
  const nodes = new Map<AdminId, RuntimeMenuItem>()
  normalizeAuthorizedMenuHierarchy(menus)
    .filter(menu => menu.type === 'DIRECTORY' || hasRegisteredMenuRoute(menu.menuCode))
    .forEach(menu => {
      nodes.set(menu.id, {
        id: menu.id,
        parentId: menu.parentId,
        type: menu.type,
        key: `authorized-menu-${menu.id}`,
        title: menu.name,
        icon: menu.icon || (menu.type === 'DIRECTORY' ? 'FolderOpened' : 'Document'),
        path: getAdminMenuRouteItem(menu.menuCode)?.page.path || menu.path || undefined,
        menuCode: menu.menuCode || undefined,
        sortOrder: menu.sortOrder,
        children: []
      })
    })

  const roots: RuntimeMenuItem[] = []
  nodes.forEach(node => {
    if (node.parentId === '0') roots.push(node)
    else nodes.get(node.parentId)?.children?.push(node)
  })

  const findScopeRoot = (scope: 'business' | 'countrySite') =>
    scope === 'countrySite'
      ? roots.find(root => root.path === '/country-site' || root.title === '国家站点管理后台' || root.title === '国家站点管理')
      : roots.find(
          root =>
            root.path === '/business' ||
            root.title === '中台业务管理' ||
            root.title === '中台管理' ||
            root.title === '中台管理后台' ||
            root.title === '中台业务管理系统'
        )
  const isDescendantOf = (node: RuntimeMenuItem, ancestor: RuntimeMenuItem) => {
    let current: RuntimeMenuItem | undefined = node
    while (current) {
      if (current.id === ancestor.id) return true
      current = nodes.get(current.parentId)
    }
    return false
  }
  const ensureScopedParent = (scope: 'business' | 'countrySite', parentTitle: string) => {
    const scopeRoot = findScopeRoot(scope)
    if (!scopeRoot) return undefined
    const catalogParent = adminMenuCatalog[scope].find(item => item.title === parentTitle && item.children?.length)
    const catalogChildCodes = (catalogParent?.children || [])
      .map(item => item.menuCode)
      .filter((menuCode): menuCode is string => Boolean(menuCode))
    const existing = Array.from(nodes.values()).find(
      node =>
        node.type === 'DIRECTORY' &&
        isDescendantOf(node, scopeRoot) &&
        (node.title === parentTitle ||
          node.children?.some(child => catalogChildCodes.some(code => isSameAdminMenuCode(child.menuCode, code))))
    )
    if (existing) return existing

    if (!catalogParent) return undefined
    const id = `frontend-only:${scope}:${catalogParent.key}`
    const parent: RuntimeMenuItem = {
      ...catalogParent,
      id,
      parentId: scopeRoot.id,
      type: 'DIRECTORY',
      children: []
    }
    nodes.set(id, parent)
    scopeRoot.children?.push(parent)
    return parent
  }

  // 兼容旧库中的业务活动码和直接使用接口权限码的菜单记录，将已有角色授权
  // 迁到国家站点活动目录，避免菜单迁移后入口被路由白名单提前过滤。
  Array.from(nodes.values()).forEach(node => {
    if (node.type !== 'MENU' || !node.menuCode) return
    const routeItem = getMenuRouteItems().find(
      item =>
        item.scope === 'countrySite' && item.parentTitle === '活动管理' && isSameAdminMenuCode(node.menuCode, item.page.menuCode)
    )
    if (!routeItem?.parentTitle || !routeItem.page.path) return
    const parent = ensureScopedParent(routeItem.scope, routeItem.parentTitle)
    if (!parent || node.parentId === parent.id) return
    nodes.get(node.parentId)!.children = nodes.get(node.parentId)!.children?.filter(child => child.id !== node.id)
    node.parentId = parent.id
    node.path = routeItem.page.path
    node.sortOrder = routeItem.page.sortOrder
    parent.children?.push(node)
  })

  getFrontendOnlyMenuRouteItems().forEach(({ scope, page, parentTitle }) => {
    const fallbackCodes = [page.authorizationFallbackCode, ...(page.authorizationFallbackCodes || [])].filter(
      (code): code is string => Boolean(code)
    )
    const hasWildcardPermission = permissions.includes('*')
    const hasFallbackAuthorization = fallbackCodes.some(
      code =>
        hasWildcardPermission ||
        permissions.some(permission => isSameAdminMenuCode(permission, code)) ||
        Array.from(nodes.values()).some(node => isSameAdminMenuCode(node.menuCode, code))
    )
    if (!hasFallbackAuthorization) return
    if (Array.from(nodes.values()).some(node => isSameAdminMenuCode(node.menuCode, page.menuCode))) return

    const parent = parentTitle
      ? ensureScopedParent(scope, parentTitle)
      : scope === 'countrySite'
        ? Array.from(nodes.values()).find(node => node.type === 'DIRECTORY' && node.title === '国家站点管理') ||
          roots.find(root => root.path === '/country-site' || root.title === '国家站点管理后台' || root.title === '国家站点管理')
        : roots.find(
            root =>
              root.path === '/business' ||
              root.title === '中台业务管理' ||
              root.title === '中台管理' ||
              root.title === '中台管理后台' ||
              root.title === '中台业务管理系统'
          )
    if (!parent || !page.path || !page.menuCode) return
    const id = `frontend-only:${page.key}`
    const node: RuntimeMenuItem = {
      id,
      parentId: parent.id,
      type: 'MENU',
      key: page.key,
      title: page.title,
      icon: page.icon,
      path: page.path,
      menuCode: page.menuCode,
      sortOrder: page.sortOrder,
      children: []
    }
    nodes.set(id, node)
    parent.children?.push(node)
  })

  const sortTree = (items: RuntimeMenuItem[]) => {
    items.sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id))
    items.forEach(item => sortTree(item.children || []))
  }
  sortTree(roots)
  return pruneEmptyDirectories(roots)
}

function pruneEmptyDirectories(items: RuntimeMenuItem[]): RuntimeMenuItem[] {
  return items.flatMap(item => {
    const children = pruneEmptyDirectories(item.children || [])
    if (item.type === 'DIRECTORY' && children.length === 0) return []
    return [{ ...item, children }]
  })
}

function changeSiteContext(siteId: CenterId | number) {
  siteContextStore.selectSite(String(siteId))
}

function changeTimezone(timezoneId: string) {
  if (!timezoneStore.selectTimezone(timezoneId)) return
  ElMessage.success(`展示时区已切换为 ${timezoneStore.currentDisplayName}`)
}

async function handleLogout() {
  try {
    await ElMessageBox.confirm('确定要退出当前登录吗？', '退出确认', {
      confirmButtonText: '退出',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    return
  }

  try {
    await authStore.logout()
    ElMessage.success('已安全退出')
  } catch (error) {
    ElMessage.warning(`${getErrorMessage(error, '退出接口请求失败')}，本地登录状态已清除`)
  } finally {
    await router.replace('/login')
  }
}

async function closeTab(path: string) {
  const tabs = openMenuTabs.value
  const closedIndex = tabs.findIndex(tab => tab.path === path)
  if (closedIndex < 0) return

  const wasActive = route.path === path
  uiStore.closeMenuTab(path)
  if (!wasActive) return

  const remaining = openMenuTabs.value
  const target = remaining[Math.min(closedIndex, remaining.length - 1)]?.path || ADMIN_WELCOME_PATH
  await router.replace(target)
}

async function closeAllTabs() {
  if (!openMenuTabs.value.length) return
  uiStore.closeAllMenuTabs()
  if (route.path !== ADMIN_WELCOME_PATH) await router.replace(ADMIN_WELCOME_PATH)
}

function scrollTabsHorizontally(event: globalThis.WheelEvent) {
  const tabsList = tabsListRef.value
  if (!tabsList || tabsList.scrollWidth <= tabsList.clientWidth || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return

  event.preventDefault()
  tabsList.scrollLeft += event.deltaY
}

async function scrollActiveTabIntoView() {
  await nextTick()
  tabsListRef.value?.querySelector<HTMLElement>('.page-tab.active')?.scrollIntoView({
    block: 'nearest',
    inline: 'nearest'
  })
}

function getMenuTabCacheName(path: string) {
  const normalizedPath = path.replace(/[^A-Za-z0-9]/g, '_')
  return `AdminCachedPage_${menuPageCacheScope.value}_${normalizedPath}`
}

function resolveCachedRouteComponent(routeComponent: VNode, path: string) {
  const cacheKey = `${menuPageCacheScope.value}:${path}`
  const existing = cachedRouteComponents.get(cacheKey)
  if (existing?.source === routeComponent.type) return existing.component

  const component = markRaw(
    defineComponent({
      name: getMenuTabCacheName(path),
      inheritAttrs: false,
      props: {
        routeComponent: {
          type: Object as PropType<VNode>,
          required: true
        }
      },
      setup(props) {
        // KeepAlive 内的 useRoute 只响应本标签；其他编辑页不能替换其业务 ID 和表单模式。
        const tabRoute = shallowRef(router.currentRoute.value)
        const cachedRoute = {} as RouteLocationNormalizedLoaded
        for (const key of Object.keys(tabRoute.value)) {
          Object.defineProperty(cachedRoute, key, {
            enumerable: true,
            get: () => tabRoute.value[key as keyof RouteLocationNormalizedLoaded]
          })
        }
        provide(routeLocationKey, shallowReactive(cachedRoute))
        watch(
          router.currentRoute,
          nextRoute => {
            if (nextRoute.path === path) tabRoute.value = nextRoute
          },
          { flush: 'sync' }
        )
        return () => cloneVNode(props.routeComponent)
      }
    })
  )
  cachedRouteComponents.set(cacheKey, { source: routeComponent.type, component })
  return component
}

function menuTabCacheKey(path: string) {
  return `${menuPageCacheScope.value}:${siteContextStore.currentSiteId || 'no-site'}:${path}`
}

watch(
  () => siteContextStore.currentSiteId,
  (siteId, previousSiteId) => {
    if (siteId === previousSiteId) return
    menuPageCacheScope.value += 1
    cachedRouteComponents.clear()
  }
)

watch(
  () => [route.fullPath, currentPageTitle.value, currentParentTitle.value] as const,
  () => {
    const pageTitle = currentPageTitle.value
    if (route.meta.system === 'business') {
      uiStore.openMenuTab({
        path: route.path,
        fullPath: route.fullPath,
        title: pageTitle,
        parentTitle: currentParentTitle.value
      })
    }
    document.title = `${pageTitle} - ${currentSystemLabel.value}`
    void scrollActiveTabIntoView()
  },
  { immediate: true }
)

watch(authorizedMenuRoots, syncOpenMenuTabNames, { immediate: true })

siteContextStore.fetchOptions().catch(error => {
  ElMessage.warning(getErrorMessage(error, '获取站点选项失败'))
})
timezoneStore.fetchOptions().catch(error => {
  ElMessage.warning(getErrorMessage(error, '获取时区选项失败'))
})
</script>
<style lang="scss" scoped>
.sidebar-brand {
  display: flex;
  flex: 0 0 52px;
  gap: 8px;
  align-items: center;
  min-width: 0;
  height: 52px;
  padding: 0 8px;
}

.sidebar-brand img {
  display: block;
  width: 142px;
  height: auto;
  filter: invert(1);
  mix-blend-mode: screen;
}

.sidebar-brand-badge {
  padding: 1px 5px;
  font-size: 10px;
  font-weight: 700;
  color: #cbbfff;
  letter-spacing: 0.04em;
  background: rgb(100 61 231 / 18%);
  border: 1px solid rgb(176 155 255 / 40%);
  border-radius: 3px;
}

.sidebar-monogram {
  display: grid;
  width: 34px;
  height: 34px;
  margin: 0 auto;
  place-items: center;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  background: var(--el-color-primary);
  border-radius: 9px;
  box-shadow: 0 6px 14px rgb(0 0 0 / 24%);
}

.sidebar-section-label {
  flex: 0 0 auto;
  margin: 19px 10px 8px;
  font-size: 11px;
  font-weight: 650;
  color: var(--admin-sidebar-muted);
  letter-spacing: 0.08em;
}

.sidebar-profile {
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  align-items: center;
  flex-wrap: nowrap;
  min-height: 58px;
  padding: 12px 8px 0;
  margin-top: 12px;
  border-top: 1px solid rgb(255 255 255 / 9%);
}

.sidebar-profile-avatar {
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  place-items: center;
  font-size: 11px;
  font-weight: 750;
  color: #fff;
  background: linear-gradient(135deg, #835ff0, #643de7);
  border: 1px solid rgb(255 255 255 / 15%);
  border-radius: 50%;
}

.sidebar-profile-copy {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
}

.sidebar-profile :deep(.el-dropdown) {
  display: inline-flex;
  flex: 0 0 auto;
}

.sidebar-profile-copy strong,
.sidebar-profile-copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-profile-copy strong {
  font-size: 12px;
  color: #fff;
}

.sidebar-profile-copy span {
  margin-top: 2px;
  font-size: 11px;
  color: var(--admin-sidebar-muted);
}

.sidebar-profile-menu {
  display: grid;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  margin-left: auto;
  place-items: center;
  font-size: 17px;
  color: var(--admin-sidebar-muted);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 7px;
}

.sidebar-profile-menu:hover,
.sidebar-profile-menu:focus-visible {
  color: #fff;
  background: rgb(255 255 255 / 8%);
  outline: none;
}

.sidebar.collapsed .sidebar-profile {
  gap: 5px;
  flex-direction: column;
  padding-right: 0;
  padding-left: 0;
}

.sidebar.collapsed .sidebar-profile-menu {
  margin-left: 0;
}

:global(.sidebar-user-dropdown .el-dropdown-menu) {
  min-width: 132px;
}

:global(.sidebar-user-dropdown .el-dropdown-menu__item) {
  gap: 8px;
}

.topbar-context {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.topbar-brand {
  display: flex;
  gap: 14px;
  align-items: center;
  min-width: 0;
  line-height: 1.5;
}

.topbar-title {
  overflow: hidden;
  font-size: 20px;
  font-weight: 700;
  color: var(--el-text-color-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topbar-scope-badge {
  flex: 0 0 auto;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 500;
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  border-radius: 999px;
}

.topbar-divider {
  flex: 0 0 1px;
  width: 1px;
  height: 30px;
  background: var(--admin-border);
}

.site-context-switcher,
.timezone-switcher {
  min-height: 36px;
  padding: 0 4px;
  border: 0;
}

.site-context-switcher strong {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switcher-label {
  color: var(--el-text-color-secondary) !important;
  white-space: nowrap;
}

.switcher-leading-icon {
  margin-right: 0 !important;
}

.switcher-caret {
  margin-left: 18px !important;
  font-size: 13px !important;
}

:global(.timezone-dropdown-popper .timezone-dropdown-menu) {
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

:global(.site-context-dropdown-menu) {
  min-width: 240px;
  max-height: 360px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

:global(.site-context-country) {
  padding: 8px 16px 5px;
  color: var(--el-text-color-placeholder);
  font-size: 12px;
  line-height: 1.5;
}

.main-content-welcome {
  padding: clamp(24px, 3vw, 40px);
}

.page-tabs {
  display: flex;
  flex: 0 0 44px;
  align-items: center;
  width: 100%;
  height: 44px;
  min-width: 0;
  min-height: 44px;
  padding: 7px 20px;
  overflow: hidden;
  background: var(--admin-surface);
  border-bottom: 1px solid var(--admin-border);
}

.page-tabs-list {
  display: flex;
  flex: 1 1 auto;
  gap: 6px;
  align-items: center;
  height: 30px;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}

.page-tabs-list::-webkit-scrollbar {
  display: none;
}

.page-tab {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 7px;
  align-items: center;
  height: 30px;
  padding: 0 10px;
  overflow: hidden;
  font-size: 13px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
  cursor: pointer;
  background: var(--admin-surface);
  border: 1px solid var(--admin-border);
  border-radius: 8px;
  box-shadow: none;
  transition: all 160ms ease;
}

.page-tab:hover {
  color: var(--el-color-primary);
  border-color: var(--el-color-primary-light-5);
}

.page-tab.active {
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  border-color: var(--el-color-primary-light-7);
}

.page-tab-dot {
  width: 6px;
  height: 6px;
  background: var(--el-border-color);
  border-radius: 50%;
}

.page-tab.active .page-tab-dot {
  background: var(--el-color-primary);
  box-shadow: 0 0 0 3px var(--el-color-primary-light-9);
}

.page-tab-close {
  margin-left: 1px;
  color: var(--el-text-color-placeholder);
  border-radius: 50%;
}

.page-tab-close:hover {
  color: #fff;
  background: var(--el-color-danger);
}

.close-all-tabs {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 5px;
  align-items: center;
  height: 30px;
  padding: 0 10px;
  margin-left: 10px;
  font-size: 13px;
  color: var(--el-text-color-regular);
  cursor: pointer;
  background: var(--admin-surface);
  border: 1px solid var(--admin-border);
  border-radius: 8px;
}

.close-all-tabs:hover:not(:disabled) {
  color: var(--el-color-danger);
  border-color: var(--el-color-danger-light-5);
}

.close-all-tabs:disabled {
  color: var(--el-text-color-placeholder);
  cursor: not-allowed;
  background: var(--el-fill-color-lighter);
}

@media (max-width: 760px) {
  .topbar-context {
    gap: 6px;
  }

  .topbar-brand {
    gap: 6px;
  }

  .topbar-title {
    max-width: 180px;
    font-size: 16px;
  }

  .topbar-divider,
  .switcher-label {
    display: none;
  }

  .switcher-caret {
    margin-left: 4px !important;
  }

  .site-context-switcher strong {
    max-width: 110px;
  }

  .close-all-tabs span {
    display: none;
  }
}
</style>
